import { useState } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    addConditionalSplitSends,
    addRandomSplitSends,
    removeBranchDescendants,
    removeConditionalSplit,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { getEffectiveSplitType } from 'domains/campaigns/components/CustomCampaign/utils';
import { ADD_SPLIT_NODE } from 'domains/campaigns/state/actionTypes';
import { SplitTypes } from 'domains/campaigns/types';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

import { getSteps, MAX_SENDS_AMOUNT, renderChooseStepsForm } from '../utils';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseSendTypeStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const {
        randomSplit: hasRandomSplitFlag,
        multipleSendsConditionalSplit: hasMultipleSendsConditionalSplitFlag,
    } = useFlags();
    const dispatch = useAppDispatch();
    const splitNodes = useAppSelector((state) => state.curation.conditionalSplit.splitNodes);
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const isEditingPausedCampaign = () => status === CampaignStatus.PAUSED;

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const effectiveSplitType = getEffectiveSplitType(activeSend, campaignState.sends);
    const isInsideConditionalSplit = !!effectiveSplitType;

    const parentSend = activeSend?.parentIndex != null ? (campaignState.sends[activeSend.parentIndex] as any) : null;
    const parentEffectiveSplitType = parentSend ? getEffectiveSplitType(parentSend, campaignState.sends) : undefined;
    // Explicit check for random split branch roots: parent has no splitType (is a plain send).
    // This is more robust than comparing effective split types, which can break when a nested
    // random split send shares the same splitType as an ancestor's effective type.
    const isRandomSplitBranchRoot =
        (activeSend?.splitType === SplitTypes.RANDOM_GROUP_1 || activeSend?.splitType === SplitTypes.RANDOM_GROUP_2) &&
        !parentSend?.splitType;
    const isBranchRoot =
        (!!activeSend?.splitType && parentEffectiveSplitType !== activeSend?.splitType) || isRandomSplitBranchRoot;

    const getIsStepDisabled = (step: CustomCampaignSteps) => {
        // For paused curations: enable only the option that matches the current send type
        if (isEditingPausedCampaign()) {
            const isRandomSplit =
                activeSend?.splitType === SplitTypes.RANDOM_GROUP_1 ||
                activeSend?.splitType === SplitTypes.RANDOM_GROUP_2;
            const isConditionalSplit = !isRandomSplit && !!activeSend?.splitAction && !!activeSend?.splitType;
            const isIndependentEmail = !isConditionalSplit && !isRandomSplit;

            switch (step) {
                case CustomCampaignSteps.SELECT_CONTENT:
                    return !isIndependentEmail;
                case CustomCampaignSteps.SET_TIME_DELAY:
                    return true;
                case CustomCampaignSteps.CONDITIONAL_SPLIT:
                    if (isInsideConditionalSplit && !isBranchRoot) return true;
                    return !isConditionalSplit;
                case CustomCampaignSteps.RANDOM_SPLIT:
                    if (isInsideConditionalSplit && !isBranchRoot) return true;
                    return !isRandomSplit;
                case CustomCampaignSteps.SCHEDULE_CURATION:
                    return false;
                default:
                    return false;
            }
        }

        // Default logic for non-paused curations
        switch (step) {
            case CustomCampaignSteps.SELECT_CONTENT:
                return false;
            case CustomCampaignSteps.SET_TIME_DELAY:
                return true;
            case CustomCampaignSteps.CONDITIONAL_SPLIT:
                // With multiple-sends-conditional-split, a split can be nested inside any branch.
                return isInsideConditionalSplit && !isBranchRoot && !hasMultipleSendsConditionalSplitFlag;
            case CustomCampaignSteps.RANDOM_SPLIT:
                return isInsideConditionalSplit && !isBranchRoot;
            case CustomCampaignSteps.SCHEDULE_CURATION:
                return true;
            default:
                return false;
        }
    };

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value as CustomCampaignSteps;

        if (getIsStepDisabled(step)) return;

        setSelectedStep(step);

        if (step === CustomCampaignSteps.SELECT_CONTENT && isBranchRoot) {
            dispatchCampaignAction(removeConditionalSplit(activeSendIndex));
        }

        if (step === CustomCampaignSteps.CONDITIONAL_SPLIT) {
            // Nesting inside an existing branch uses the normalized splitNodes model
            // (mirrors CustomCampaignChooseNextStepConditionalSplit). The legacy
            // addConditionalSplitSends path is reserved for the first/root-level split.
            if (hasMultipleSendsConditionalSplitFlag && isInsideConditionalSplit) {
                const nestedSplitAlreadyExists = campaignState.sends.some(
                    (send: any) => send.parentIndex === activeSendIndex && send.splitNodeId != null
                );
                if (!nestedSplitAlreadyExists) {
                    // Count descendants so we can compute the correct post-remove index for
                    // the new MEETS send (removeBranchDescendants shifts indices).
                    const collectDescendants = (parentIdx: number): number => {
                        let count = 0;
                        (campaignState.sends as any[]).forEach((s, i) => {
                            if (s.parentIndex === parentIdx) {
                                count++;
                                count += collectDescendants(i);
                            }
                        });
                        return count;
                    };
                    const descendantCount = collectDescendants(activeSendIndex);
                    const splitNodeId = `split_${Object.keys(splitNodes).length}`;
                    const addSplitNodeAction = {
                        type: ADD_SPLIT_NODE,
                        payload: { splitNodeId, parentSendIndex: activeSendIndex },
                    };
                    dispatchCampaignAction(removeBranchDescendants(activeSendIndex));
                    dispatchCampaignAction(addSplitNodeAction);
                    dispatch(addSplitNodeAction);
                    setActiveSendIndex(campaignState.sends.length - descendantCount);
                    setActiveStep('set-split-criteria' as CustomCampaignSteps);
                }
                return;
            }

            const doesNotMeetSendExists = campaignState.sends.some(
                (send: any, idx: number) =>
                    idx !== activeSendIndex &&
                    send.splitType === SplitTypes.DOES_NOT_MEET &&
                    (send.parentIndex ?? 0) === (activeSend?.parentIndex ?? 0)
            );

            if (!doesNotMeetSendExists) {
                dispatchCampaignAction(removeBranchDescendants(activeSendIndex));
                dispatchCampaignAction(addConditionalSplitSends(activeSendIndex));
            }
        }

        if (step === CustomCampaignSteps.RANDOM_SPLIT) {
            const randomGroupSendsExist = campaignState.sends.some(
                (send: any) =>
                    (send.splitType === SplitTypes.RANDOM_GROUP_1 || send.splitType === SplitTypes.RANDOM_GROUP_2) &&
                    send.parentIndex === activeSendIndex
            );
            if (!randomGroupSendsExist) {
                dispatchCampaignAction(removeBranchDescendants(activeSendIndex));
                dispatchCampaignAction(addRandomSplitSends(activeSendIndex));
            }
        }

        setActiveStep(step);
    };

    const maxSendsReached = campaignState.sends.length === MAX_SENDS_AMOUNT;
    const steps = getSteps(
        getIsStepDisabled,
        maxSendsReached,
        isEditingPausedCampaign(),
        'Conditional splits cannot be nested inside an existing split branch.',
        !!hasRandomSplitFlag,
        undefined,
        !!campaignState.isMixedChannel
    );

    return renderChooseStepsForm(handleStepClick, selectedStep, steps);
};

export default CustomCampaignChooseSendTypeStep;
